import {
  AuthenticationResult,
  Configuration,
  LogLevel,
  PublicClientApplication,
} from "@azure/msal-browser";
import { stringUtils } from "cayo.ui";
import { identityProviders, AuthMode } from "@cayo/shared/api/types/sso";
import { appSignals } from "@cayo/shared/components/App/utils";
import logger from "@cayo/shared/libs/logger";
import { endpoints } from "@cayo/shared/services/endpoints.service";
import { authContextStore } from "@cayo/shared/settings/authContextStore";
import tokenStorage from "@cayo/shared/settings/tokenStorage";
import { IAuthorizationServerOptions, OAuthClient } from "@cayo/shared/types/manifest";
import downloadUtils from "@cayo/shared/utils/download";
import { queryUtils } from "@cayo/shared/utils/query";
import { urlUtils } from "@cayo/shared/utils/url";
import redirectUtils from "../components/routing/redirect-utils";

export function isValidAuthMode(value: unknown): value is AuthMode {
  return identityProviders.includes(value as (typeof identityProviders)[number]);
}

export type SsoSilentTokenResult =
  | { type: "success"; response: AuthenticationResult }
  | { type: "failed"; error: unknown };

const log = logger.getLogger("SSO");

export class SSO {
  private readonly app: PublicClientApplication;
  public readonly ready: Promise<AuthenticationResult | null>;

  public static readonly scopes = ["openid", "profile", "adminapi"];
  private readonly defaultClientId = "csm.client.web";

  constructor(oauthClient?: OAuthClient, auth_server?: IAuthorizationServerOptions) {
    const authorityEndpoint =
      auth_server?.authorityUri || stringUtils.trim(endpoints.apiEndpoint + endpoints.PUBLIC_URL);

    const msalConfig: Configuration = this.BuildMsalConfig(oauthClient, authorityEndpoint);

    this.app = new PublicClientApplication(msalConfig);
    this.ready = this.initializeAndHandleRedirect();
  }

  private BuildMsalConfig(oauthClient: OAuthClient | undefined, authorityEndpoint: string) {
    const msalConfig: Configuration = {
      auth: {
        clientId: oauthClient?.clientId || this.defaultClientId,
        authority: authorityEndpoint,
        knownAuthorities: [authorityEndpoint],
      },
      system: {
        loggerOptions: {
          loggerCallback: (level, message) => {
            const msalLog = logger.getLogger("MSAL");
            switch (level) {
              case LogLevel.Error:
                msalLog.error(message);
                break;
              case LogLevel.Warning:
                msalLog.warn(message);
                break;
              case LogLevel.Info:
                msalLog.info(message);
                break;
              default:
                msalLog.debug(message);
                break;
            }
          },
          logLevel: LogLevel.Verbose,
        },
      },
    };

    log.debug("msalConfig", msalConfig);
    return msalConfig;
  }

  private async initializeAndHandleRedirect(): Promise<AuthenticationResult | null> {
    try {
      await this.app.initialize();
      const response = await this.app.handleRedirectPromise({ navigateToLoginRequestUrl: false });
      this.persistAuthenticationResult(response);
      return response;
    } catch (error) {
      log.debug("handleRedirectPromise, error", error);
      return null;
    }
  }

  public static useExternalAuth(authMode: AuthMode) {
    authContextStore.setSsoLoginContext(authMode);

    appSignals.send("handleAuth", { authMode });
  }

  public persistAuthenticationResult(response: AuthenticationResult | null) {
    if (!response) {
      return;
    }

    log.debug("handleRedirectPromise, response", response);
    if (response.accessToken) {
      tokenStorage.setSsoToken(response);
    }

    try {
      const claims = response.account?.idTokenClaims as any;
      const loginHint = claims?.["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"];
      if (loginHint) {
        log.debug("handleRedirectPromise, loginHint found", loginHint);
        authContextStore.setLoginHint(loginHint);
      }
    } catch (e) {
      log.error("handleRedirectPromise, failed to get user name, error", e);
    }
  }

  public async refreshTokenSilent(opts: { authorityUri?: string }): Promise<SsoSilentTokenResult> {
    await this.ready;

    const account = this.app.getAllAccounts()[0];
    const extraQueryParameters: Record<string, string> = {
      acr_values: `idp:${authContextStore.get().authMode}`,
    };
    const authority = opts.authorityUri || undefined;

    try {
      const response = await this.app.acquireTokenSilent({
        scopes: SSO.scopes,
        extraQueryParameters,
        account: account || undefined,
        authority,
      });

      return { type: "success", response };
    } catch (error) {
      return { type: "failed", error };
    }
  }

  // Legacy - need to move Download flow before switch to new retry flow
  public async acquireTokenOrRedirect(opts: {
    loginHint?: string;
    redirectUri: string;
    currentHash: string;
    isIFrame: boolean;
    authorityUri?: string;
  }): Promise<{ type: "token"; redirectUrl: string } | { type: "download"; file: string } | void> {
    const silentResult = await this.refreshTokenSilent({ authorityUri: opts.authorityUri });

    if (silentResult.type === "success") {
      const response = silentResult.response;

      log.debug("acquireTokenSilent success");
      tokenStorage.setSsoToken(response);

      const redirectUrl = redirectUtils.getRelativeRedirectUrl();
      let file = queryUtils.getQueryParam("file", redirectUrl);

      const storedDownload = localStorage.getItem("download");
      if (!file && storedDownload) {
        file = storedDownload;
        log.debug(`get download file ${file} from local storage`);
      }

      if (file) {
        log.debug(`file to download detected: ${file}`);
        downloadUtils.downloadODataFile(file);
        localStorage.setItem("download", file);
        return { type: "download", file };
      }

      log.debug("acquireTokenSilent success, redirect to", redirectUrl);
      return { type: "token", redirectUrl };
    }

    const e: any = silentResult.error;
    const authority = opts.authorityUri || undefined;
    const extraQueryParameters: Record<string, string> = {
      acr_values: `idp:${authContextStore.get().authMode}`,
    };

    log.debug("silent authorization failed", e);

    const file = queryUtils.getQueryParam("file");
    if (file) {
      log.debug("file", file, "hash", window.location.hash);
      localStorage.setItem(
        redirectUtils.redirectParameterName,
        urlUtils.trim(window.location.hash)
      );
    }

    log.debug("isSrvEndpoint", opts.redirectUri);

    const prompt = e.errorCode === "login_required" || !opts.loginHint ? "select_account" : "none";

    // wait until redirect resolves, code that goes after that won't be executed
    await this.app.loginRedirect({
      scopes: SSO.scopes,
      redirectUri: opts.redirectUri,
      prompt,
      loginHint: opts.loginHint,
      extraQueryParameters,
      authority,
    });
  }
}

let ssoInstance: SSO | undefined;

export function createSSO(
  oauthClient?: OAuthClient,
  auth_server?: IAuthorizationServerOptions
): SSO {
  ssoInstance = new SSO(oauthClient, auth_server);
  return ssoInstance;
}

export function getSSO(): SSO {
  if (!ssoInstance) {
    throw new Error("SSO not initialized. Call createSSO() first.");
  }
  return ssoInstance;
}

export default SSO;
